// The day view's reveal — one clock, one curve, every element a segment of it.
//
// Pure functions and constants only: no React, no DOM, no timers. Food.js owns
// the clock (a single performance.now() stamped when a day's content lands)
// and every animated element derives its own progress from that one origin —
// which is why the ring's digits land exactly when its arc stops: they are two
// readings of the same value, not two timers.
//
// The CSS half of the choreography (fades, rises, staggers) lives in Food.css
// under .Food-day--arrive and hardcodes the same numbers; a change to a delay
// here without its CSS twin desynchronizes the reveal. Both sides are listed
// in the table below so the pairing is checkable in one place.
//
//   0ms          ring arc + kcal digits        JS   SEGMENTS.ring
//   120 + 40i    hero centre/right, macros     CSS  Food.css (arrive block)
//   200ms        burn bar sweep, pill, tick    JS   SEGMENTS.bar
//   320 + 50i    mineral tiles rise            CSS  mineralRevealDelayMs(i)
//   320 + 50i    mineral digits                JS   mineralCountSegment(i)
//   450ms        protein run-rate fill         CSS  Food.css (arrive block)
//   520 + 30i    ledger rows (i capped at 6)   CSS  rowRevealDelayMs(i)
//
// Everything settles by REVEAL_TOTAL_MS. Nothing loops.

// The whole sequence's length: the moment after which a mount is a re-mount
// (a save's refetch, a remount mid-day) and must render settled, not arrive
// again. The last row lands at 520 + 6×30 + 180 = 880.
export const REVEAL_TOTAL_MS = 880;

// The ONE easing curve — easeOutCubic. CSS states the same curve as
// cubic-bezier(0.33, 1, 0.68, 1). Deceleration only: an eased count never
// overshoots, so no intermediate frame ever displays a number larger than
// the real one.
export function easeOut(t) {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - clamped, 3);
}

// The JS-driven segments. start/duration in ms from the shared origin.
export const SEGMENTS = {
  ring: { start: 0, duration: 600 },
  bar: { start: 200, duration: 450 },
};

export const MINERAL_REVEAL_START_MS = 320;
export const MINERAL_REVEAL_STAGGER_MS = 50;
export const MINERAL_COUNT_MS = 400;

export function mineralRevealDelayMs(i) {
  return MINERAL_REVEAL_START_MS + i * MINERAL_REVEAL_STAGGER_MS;
}

export function mineralCountSegment(i) {
  return { start: mineralRevealDelayMs(i), duration: MINERAL_COUNT_MS };
}

// Ledger rows: 30ms stagger, capped so a long day does not serialize — from
// the seventh row on, everything arrives together and the sequence still
// ends inside REVEAL_TOTAL_MS on a 20-row day.
export const ROW_REVEAL_START_MS = 520;
export const ROW_REVEAL_STAGGER_MS = 30;
export const ROW_REVEAL_STAGGER_CAP = 6;

export function rowRevealDelayMs(i) {
  return ROW_REVEAL_START_MS + Math.min(i, ROW_REVEAL_STAGGER_CAP) * ROW_REVEAL_STAGGER_MS;
}

// Eased progress through one segment at a given elapsed time: 0 before it
// starts, 1 after it ends, the shared curve in between.
export function segmentProgress(elapsedMs, { start, duration }) {
  if (elapsedMs <= start) return 0;
  if (elapsedMs >= start + duration) return 1;
  return easeOut((elapsedMs - start) / duration);
}
